import { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Spin, Empty, Row, Col, Button, Flex } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";

import Header from "../../components/common/Layouts/Header";
import Footer from "../../components/common/Layouts/Footer";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import EventCard from "../../components/Events/EventCard";
import {
  fetchEventsByCategory,
  fetchCategoryBySlug,
  selectCategoryEvents,
  selectFetchCategoryEventsStatus,
  selectFetchCategoryEventsError,
  selectCurrentCategoryBySlug,
  selectFetchCategoryBySlugStatus,
  selectFetchCategoryBySlugError,
  clearCategoryEvents,
} from "../../redux/eventSlice";

export default function CategoryEventsPage() {
  const { slug } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const categoryEvents = useSelector(selectCategoryEvents);
  const eventsLoadingStatus = useSelector(selectFetchCategoryEventsStatus);
  const eventsError = useSelector(selectFetchCategoryEventsError);
  const currentCategory = useSelector(selectCurrentCategoryBySlug);
  const categoryLoadingStatus = useSelector(selectFetchCategoryBySlugStatus);
  const categoryError = useSelector(selectFetchCategoryBySlugError);

  useEffect(() => {
    if (!slug) return;

    dispatch(clearCategoryEvents());
    dispatch(fetchCategoryBySlug(slug));
    dispatch(fetchEventsByCategory(slug));

    return () => {
      dispatch(clearCategoryEvents());
    };
  }, [dispatch, slug]);

  const isLoading =
    categoryLoadingStatus === "loading" || eventsLoadingStatus === "loading";
  const notFound =
    categoryLoadingStatus === "completed" && !currentCategory && !categoryError;
  const pageTitle = currentCategory?.name
    ? `${currentCategory.name} Events`
    : "Category Events";

  return (
    <>
      <HelmetWrapper
        title={`${pageTitle} — Pingvite`}
        description={`Explore upcoming ${currentCategory?.name || ""} events near you on Pingvite.`}
      />

      <Header />

      <main className="categoryEventsPage">
        <div className="container">
          <div className="categoryEventsNav">
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
              className="categoryEventsBackBtn"
            >
              Back
            </Button>
            <span className="categoryEventsBreadcrumb">
              <Link to="/">Home</Link>
              <span className="breadcrumbSep">/</span>
              <span>Categories</span>
              <span className="breadcrumbSep">/</span>
              <span>{currentCategory?.name || "Events"}</span>
            </span>
          </div>

          <h1 className="categoryEventsTitle">{pageTitle}</h1>

          {currentCategory && (
            <p className="categoryEventsSubtitle">
              Showing all upcoming {currentCategory.name.toLowerCase()} events
            </p>
          )}

          <Spin spinning={isLoading} tip="Loading events...">
            {!eventsError && !categoryError && categoryEvents.length > 0 ? (
              <Flex vertical gap={32}>
                <Row gutter={[20, 20]}>
                  {categoryEvents.map((event) => (
                    <Col xs={24} sm={12} lg={6} key={event.id}>
                      <div
                        style={{ cursor: "pointer" }}
                        onClick={() => event.slug && navigate(`/event-details/${event.slug}`)}
                      >
                        <EventCard event={event} />
                      </div>
                    </Col>
                  ))}
                </Row>
              </Flex>
            ) : (
              !isLoading && (
                <Empty
                  description={
                    categoryError || eventsError
                      ? "Failed to load events. Please try again."
                      : notFound
                        ? "Category not found."
                        : `No upcoming ${currentCategory?.name || ""} events found.`
                  }
                />
              )
            )}
          </Spin>
        </div>
      </main>

      <Footer />
    </>
  );
}
